(function () {
  'use strict';

  /** App proxy: use events-v endpoint. */
  const PROXY_BASE = '/apps/workshop-events';
  const PROXY_BASE_FALLBACK = '/apps/workshop-event';
  const EVENTS_PATH = '/events-v';
  const DEV_MODE = typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true);

  const CACHE_TTL_MS = 5 * 60 * 1000;
  const CACHE_KEY_PREFIX = 'workshop_events_';
  let eventsAbortController = null;
  let eventsRequestId = 0;

  function getCityKey() {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('city_key');
    if (fromQuery && String(fromQuery).trim()) return String(fromQuery).trim();
    const el = document.querySelector('[data-city-key]');
    if (el && el.getAttribute('data-city-key')) return String(el.getAttribute('data-city-key')).trim();
    return '';
  }

  function getContainer() {
    return document.querySelector('[data-workshop-events-root]');
  }

  function setStatus(root, text, kind) {
    const statusEl = root.querySelector('[data-workshop-events-status]');
    const listEl = root.querySelector('[data-workshop-events-list]');
    if (statusEl) statusEl.textContent = text || '';
    if (statusEl) statusEl.className = 'workshop-events-status workshop-events-status--' + (kind || 'idle');
    if (listEl) listEl.innerHTML = '';
  }

  function setList(root, html) {
    const listEl = root.querySelector('[data-workshop-events-list]');
    if (listEl) listEl.innerHTML = html;
  }

  /** Event time display: ONLY ev.time_label. Wall-clock: never convert to local; no Date/Intl/toLocale*, no parsing of starts_at. */
  var _workshopTimeLabelWarned = (typeof Set !== 'undefined') ? new Set() : {};
  function getEventTimeLabel(ev) {
    if (!ev || typeof ev !== 'object') return '';
    var t = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
    if (!t) {
      var key = (ev.id != null ? String(ev.id) : '') || (ev.handle != null ? String(ev.handle) : '') || '';
      if (key && !_workshopTimeLabelWarned.has(key)) {
        _workshopTimeLabelWarned.add(key);
        console.warn('[workshop] time_label missing for event', ev.id || ev.handle || '');
      }
    }
    return t;
  }

  function getCacheKey(cityKey) {
    return CACHE_KEY_PREFIX + cityKey;
  }

  function readCache(cityKey) {
    try {
      const key = getCacheKey(cityKey);
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.cachedAt || !data.payload) return null;
      if (Date.now() - data.cachedAt > CACHE_TTL_MS) return null;
      return data.payload;
    } catch (_) {
      return null;
    }
  }

  function writeCache(cityKey, payload) {
    try {
      const key = getCacheKey(cityKey);
      sessionStorage.setItem(key, JSON.stringify({ cachedAt: Date.now(), payload }));
    } catch (_) {}
  }

  function fetchEvents(endpoint, cityKey) {
    if (eventsAbortController) eventsAbortController.abort();
    eventsAbortController = new AbortController();
    const requestId = ++eventsRequestId;
    let qs = 'city_key=' + encodeURIComponent(cityKey);
    if (DEV_MODE && qs.indexOf('debug=') === -1) qs = qs + '&debug=1';
    const url = endpoint + '?' + qs;
    const startMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
    if (DEV_MODE) console.log('[WB_EVENTS] request_id=' + requestId + ' url=' + url + ' start');
    const signal = eventsAbortController.signal;
    function doFetch(u, opts) {
      return fetch(u, opts).then(function (res) {
        if (res.status === 404 && u.indexOf(PROXY_BASE) !== -1) {
          if (DEV_MODE) console.log('[WB_EVENTS] request_id=' + requestId + ' 404 fallback');
          console.warn('[workshop-events] App proxy returned 404; retrying with legacy path');
          return doFetch(PROXY_BASE_FALLBACK + EVENTS_PATH + '?' + qs, opts);
        }
        var endMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
        if (DEV_MODE) console.log('[WB_EVENTS] request_id=' + requestId + ' status=' + res.status + ' end ms=' + Math.round(endMs - startMs) + ' aborted=false');
        return res;
      });
    }
    return doFetch(url, { method: 'GET', credentials: 'same-origin', signal: signal })
      .then(function (res) {
        if (!res.ok) throw new Error('proxy_http_error');
        return res.text();
      })
      .then(function (text) {
        var data;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          throw new Error('Invalid response');
        }
        if (!data || data.ok !== true) {
          console.warn('[workshop-events]', data && data.error_code, data && data.message);
          throw new Error((data && data.message && String(data.message).trim()) ? String(data.message).trim() : 'Invalid response');
        }
        if (DEV_MODE && data.meta && data.meta.debug && data.meta.debug.rid) console.log('[WB_EVENTS] request_id=' + requestId + ' backend_rid=' + data.meta.debug.rid);
        return { data: data, requestId: requestId };
      })
      .catch(function (err) {
        if (DEV_MODE && err && err.name === 'AbortError') console.log('[WB_EVENTS] request_id=' + requestId + ' aborted=true');
        throw err;
      });
  }

  function renderCards(events) {
    if (!Array.isArray(events) || events.length === 0) return '';
    const sorted = events.slice().sort(function (a, b) {
      const sa = (a && a.starts_at) || '';
      const sb = (b && b.starts_at) || '';
      return sa.localeCompare(sb);
    });
    return sorted
      .map(function (ev) {
        const title = (ev && ev.event_name) || 'Workshop Session';
        const t = getEventTimeLabel(ev);
        const expectedTime = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
        if (expectedTime && t !== expectedTime && typeof window !== 'undefined' && window.__WB_DIAG__) {
          console.warn('[workshop] time display must equal time_label', { id: ev.id, handle: ev.handle, time_label: ev.time_label, displayed: t });
        }
        const timeLabel = t || '—';
        const total = ev && ev.capacity_total != null ? Number(ev.capacity_total) : 0;
        const remaining = ev && ev.capacity_remaining != null ? Number(ev.capacity_remaining) : 0;
        const capacityLine = 'Remaining: ' + remaining + ' / ' + total;
        const eventId = (ev && ev.id) || '';
        const variants = (ev && ev.available_variants && Array.isArray(ev.available_variants))
          ? ev.available_variants
          : [];
        const variantsStr = JSON.stringify(variants);
        return (
          '<div class="workshop-event-card" data-workshop-event-card data-event-id="' +
          escapeAttr(eventId) +
          '" data-available-variants="' +
          escapeAttr(variantsStr) +
          '" data-time-label="' +
          escapeAttr(t || '') +
          '">' +
          '<div class="workshop-event-card__title">' +
          escapeHtml(title) +
          '</div>' +
          '<div class="workshop-event-card__datetime">' +
          escapeHtml(timeLabel) +
          '</div>' +
          '<div class="workshop-event-card__capacity">' +
          escapeHtml(capacityLine) +
          '</div>' +
          '</div>'
        );
      })
      .join('');
  }

  function escapeHtml(s) {
    if (s == null) return '';
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function run(root) {
    // Theme: city_key fetch disabled. All events requests use lat/lng + radius_km (events-v) only.
    if (typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true)) {
      console.log('[WB_EVENTS] city_key flow disabled; not fetching');
    }
    setStatus(root, '', 'empty');
    return;

    const endpoint = root.getAttribute('data-events-endpoint') || (PROXY_BASE + EVENTS_PATH);
    const cityKey = getCityKey();

    if (!cityKey) {
      setStatus(root, '', 'empty');
      return;
    }

    setStatus(root, 'Loading sessions…', 'loading');
    const cached = readCache(cityKey);
    if (cached && cached.events) {
      const html = renderCards(cached.events);
      setStatus(root, '', 'idle');
      setList(root, html || '');
      return;
    }

    fetchEvents(endpoint, cityKey)
      .then(function (payload) {
        if (!payload || payload.requestId !== eventsRequestId) return;
        var data = payload && payload.data;
        if (!data) return;
        writeCache(cityKey, data);
        const events = (data.events) || [];
        const html = renderCards(events);
        setStatus(root, events.length === 0 ? 'No sessions found for this city.' : '', events.length === 0 ? 'empty' : 'idle');
        setList(root, html || '');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        setStatus(root, (err && err.message && err.message !== 'Invalid response' && err.message !== 'proxy_http_error') ? err.message : "Couldn't load sessions. Try again.", 'error');
      });
  }

  /** Theme: city_key flow disabled. Entrypoint noop — no network request. */
  function init() {
    if (typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true)) {
      console.log('[WB_EVENTS] workshop-events.js init noop (city_key disabled)');
    }
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
